/**
 * Recherche produit de la boutique : tolérante, par mots, classée par pertinence.
 *
 * Avant (29/08/2026), la recherche était un `includes` de la phrase entière sur
 * le nom : « tomates cerises » ne trouvait pas « Tomate cerise », « carote »
 * ne trouvait rien, et « pain choc » non plus. Ici :
 *  - accents, ligatures et casse ignorés ;
 *  - chaque mot de la requête doit se retrouver (dans n'importe quel ordre)
 *    dans le nom, le rayon/la catégorie, la description, le fournisseur… ;
 *  - un mot suffit par son DÉBUT (« tom » → tomate) et au singulier comme au
 *    pluriel (« tomates » → tomate) ;
 *  - une faute de frappe est tolérée sur les mots d'au moins 4 lettres
 *    (« carote » → carotte : une lettre d'écart, dès 5 lettres) ;
 *  - les mots vides (« de », « du », « au »…) ne comptent pas ;
 *  - les résultats sont classés : mot exact du nom > début de mot du nom >
 *    catégorie > description > reste > approximation.
 */

export const normalizeSearch = (str: string): string =>
  (str || '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const STOP_WORDS = new Set([
  'de', 'du', 'des', 'le', 'la', 'les', 'l', 'd', 'au', 'aux', 'a', 'en', 'et', 'ou', 'un', 'une',
  'the', 'of', 'bio',
]);

/** Singulier approximatif : « tomates » → « tomate », « choux » → « chou » */
const stem = (word: string): string =>
  word.length > 3 && /[sx]$/.test(word) ? word.slice(0, -1) : word;

const words = (str?: string | null): string[] =>
  normalizeSearch(str || '').split(' ').filter(Boolean);

export const tokenizeQuery = (query: string): string[] => {
  const all = words(query);
  const kept = all.filter((w) => !STOP_WORDS.has(w));
  // Si la requête n'est faite que de mots vides (« de »), on garde tout.
  // Les mots restent BRUTS : le mot exact (« pâtes ») prime sur sa forme
  // au singulier (« pâte à tartiner ») au classement.
  return kept.length ? kept : all;
};

/** Distance de Damerau-Levenshtein (transposition comprise), bornée à `max`. */
const editDistance = (a: string, b: string, max: number): number => {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev2: number[] = [];
  let prev: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur: number[] = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, prev2[j - 2] + 1);
      }
      cur[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    prev2.splice(0, prev2.length, ...prev);
    prev = cur;
  }
  return prev[b.length];
};

export interface SearchableFields {
  name: string;
  /** Rang de ventes (1 = meilleure vente) : départage les égalités de pertinence. */
  rank?: number | null;
  category?: string; // chemin « Légumes > Tomates »
  description?: string | null;
  other?: string | null; // code, code-barres, fournisseur, producteur…
}

interface IndexedFields {
  name: string[];
  category: string[];
  description: string[];
  other: string[];
  nameText: string;
  /** Nom sans aucun espace : « pain hot dog » retrouve « Pain Hotdog ». */
  nameCompact: string;
}

const indexFields = (f: SearchableFields): IndexedFields => ({
  name: words(f.name),
  category: words(f.category),
  description: words(f.description),
  other: words(f.other),
  nameText: normalizeSearch(f.name),
  nameCompact: normalizeSearch(f.name).replace(/ /g, ''),
});

const matchWord = (list: string[], token: string, allowContains: boolean): number => {
  const root = stem(token);
  let best = 0;
  for (const w of list) {
    if (w === token) return 4; // mot exact
    if (stem(w) === root) best = Math.max(best, 3); // singulier ≈ pluriel
    else if (w.startsWith(root)) best = Math.max(best, 2);
    // Milieu de mot seulement à partir de 5 lettres : « oeuf » ne doit pas
    // ramener « Coeur de Boeuf »
    else if (allowContains && token.length >= 5 && w.includes(token)) best = Math.max(best, 1);
  }
  return best;
};

const fuzzyMatch = (list: string[], rawToken: string): boolean => {
  const token = stem(rawToken);
  // Dès 5 lettres seulement : à 4, « oeuf » ramenait « Coeur de Boeuf ».
  // Une seule faute : à deux, « baguette » ramenait « Barquettes ».
  if (token.length < 5) return false;
  const max = 1;
  return list.some((w) => w.length >= 3 && editDistance(stem(w), token, max) <= max);
};

/** Le mot du nom est-il exactement le mot tapé, au pluriel près ? */
const memeMot = (w: string | undefined, token: string): boolean =>
  !!w && (w === token || stem(w) === stem(token));

/** Score d'un produit pour des mots de requête déjà normalisés ; 0 = exclu. */
export const scoreFields = (fields: SearchableFields, tokens: string[]): number => {
  if (!tokens.length) return 1;
  const idx = indexFields(fields);
  let score = 0;
  let manque = false;
  for (const token of tokens) {
    const inName = matchWord(idx.name, token, true);
    if (inName) {
      score += inName === 4 ? 100 : inName === 3 ? 90 : inName === 2 ? 80 : 60;
      continue;
    }
    if (matchWord(idx.category, token, false)) { score += 50; continue; }
    if (matchWord(idx.description, token, true)) { score += 40; continue; }
    if (matchWord(idx.other, token, true)) { score += 30; continue; }
    if (fuzzyMatch(idx.name, token) || fuzzyMatch(idx.category, token)) { score += 25; continue; }
    manque = true; // un mot de la requête ne se retrouve nulle part
    break;
  }
  if (manque) {
    // Mot écrit collé ou séparé : « pain hot dog » → fiche « Pain Hotdog »,
    // « coca cola » → « Cocacola » (caisse, 30/09/2026). Comparaison sans espaces,
    // seulement pour une requête assez longue pour ne pas ramener n'importe quoi.
    const compact = tokens.join('');
    if (compact.length >= 5 && idx.nameCompact.includes(compact)) {
      return 60 + (idx.nameCompact.startsWith(compact) ? 40 : 0);
    }
    return 0;
  }
  // Le nom COMMENCE par les mots tapés, en mots entiers : « lait » met « Lait
  // coco » en tête, pas « Laitue » (qui ne fait que commencer par les mêmes lettres).
  if (tokens.every((t, k) => memeMot(idx.name[k], t))) score += 40;
  // L'expression tapée figure telle quelle dans le nom : « 5 céréales » met
  // « Flocons 5 Céréales » devant « Barres céréales … (5 pièces) ».
  else if (
    tokens.length > 1 &&
    idx.name.some((_w, k) => tokens.every((t, n) => memeMot(idx.name[k + n], t)))
  ) {
    score += 30;
  }
  return score;
};

/**
 * Filtre et classe des produits par pertinence.
 * `fieldsOf` fournit les champs cherchables (nom, chemin de catégorie…).
 */
export function searchProducts<T>(
  items: T[],
  query: string,
  fieldsOf: (item: T) => SearchableFields,
): T[] {
  const tokens = tokenizeQuery(query);
  if (!tokens.length) return items;
  const scored: { item: T; score: number; name: string; rank: number }[] = [];
  for (const item of items) {
    const fields = fieldsOf(item);
    const score = scoreFields(fields, tokens);
    if (score > 0) {
      scored.push({ item, score, name: fields.name, rank: fields.rank ?? Number.MAX_SAFE_INTEGER });
    }
  }
  // À pertinence égale : la meilleure vente d'abord, puis le nom le plus court
  // (le plus proche de ce qui a été tapé) — plus l'ordre alphabétique, qui
  // mettait « Tomate cerise allongée » devant « Tomate d'Elouges ».
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      a.rank - b.rank ||
      a.name.length - b.name.length ||
      a.name.localeCompare(b.name, 'fr'),
  );
  return scored.map((s) => s.item);
}
